import { fetchAuthSession } from 'aws-amplify/auth';
import loggingService from '../../services/loggingService';

const API_BASE_URL = 'https://api.innoworx.ai/Stage';

/**
 * Retrieves the authentication token from AWS Cognito.
 * @returns {Promise<string|null>} The ID token or null if not available.
 */
const getAuthToken = async () => {
  try {
    const session = await fetchAuthSession();
    const idToken = session.tokens?.idToken?.toString();

    if (idToken) {
      console.log('Purchase Order API: Token obtained successfully');
      return idToken;
    }

    console.warn('Purchase Order API: No ID token found in session');
    return null;
  } catch (error) {
    console.error('Purchase Order API: Error fetching auth session:', error);
    return null;
  }
};

/**
 * Generic function to make authenticated API requests.
 * @param {string} endpoint - The API endpoint relative to the BASE_URL.
 * @param {string} method - The HTTP method (e.g., 'GET', 'POST', 'PUT').
 * @param {Object} [body=null] - The request body for POST/PUT requests.
 * @returns {Promise<Response>} - The response object from the API.
 * @throws {Error} - Throws an error if the API request fails or returns a non-OK status.
 */
const makeApiRequest = async (endpoint, method = 'GET', body = null) => {
  const token = await getAuthToken();

  if (!token) {
    const error = new Error(
      'Authentication token is missing. Please ensure a user is logged in.'
    );
    error.code = 'AUTHENTICATION_FAILED';
    error.details =
      'No valid authentication token found. User may need to log in again.';
    throw error;
  }

  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };

  const config = {
    method: method,
    headers: headers,
  };

  if (body) {
    config.body = JSON.stringify(body);
  }

  try {
    console.log(
      `Purchase Order API: Making ${method} request to: ${API_BASE_URL}${endpoint}`
    );
    const response = await fetch(`${API_BASE_URL}${endpoint}`, config);

    if (!response.ok) {
      const responseText = await response.text();
      let errorData = {};
      try {
        errorData = responseText ? JSON.parse(responseText) : {};
      } catch {
        errorData = { message: responseText || 'Unknown error' };
      }
      
      console.error(
        `Purchase Order API Error: ${response.status} ${response.statusText} for ${endpoint}`,
        errorData
      );

      const error = new Error(
        `API Error: ${response.status} - ${
          errorData.error || errorData.message || 'Unknown error'
        }`
      );
      error.status = response.status;
      error.statusText = response.statusText;
      error.endpoint = endpoint;
      error.responseData = errorData;

      // Log API error to logging service
      loggingService.logError(
        'api_error_purchase_order',
        `Purchase Order API error: ${response.status} ${response.statusText} for ${endpoint}`,
        error,
        {
          endpoint,
          method,
          status: response.status,
          statusText: response.statusText,
          responseData: responseText,
        }
      ).catch(logError => {
        console.warn('[purchaseOrderApi] Failed to log error:', logError);
      });

      throw error;
    }

    return response;
  } catch (error) {
    if (error.code === 'AUTHENTICATION_FAILED') {
      // Log authentication error to logging service
      loggingService.logError(
        'api_error_purchase_order',
        `Purchase Order API authentication error for ${endpoint}`,
        error,
        {
          endpoint,
          method,
          errorType: 'AuthenticationError',
        }
      ).catch(logError => {
        console.warn('[purchaseOrderApi] Failed to log error:', logError);
      });
      
      throw error;
    }

    console.error(
      `Purchase Order API: Network or Fetch Error for ${endpoint}:`,
      error
    );
    error.endpoint = endpoint;
    error.method = method;

    // Log network/fetch errors to logging service
    loggingService.logError(
      'api_error_purchase_order',
      `Purchase Order API network/fetch error for ${endpoint}`,
      error,
      {
        endpoint,
        method,
        errorType: error?.name || 'NetworkError',
      }
    ).catch(logError => {
      console.warn('[purchaseOrderApi] Failed to log error:', logError);
    });

    throw error;
  }
};

/**
 * API utility functions for Purchase Order Management.
 */
export const purchaseOrderApi = {
  /**
   * Creates a purchase order header and associated line items.
   * POST /stock/purchase-orders
   * Lambda: innoworx-po-create
   * @param {Object} poData - Purchase order data
   * @param {string} poData.vendor_id - Vendor identifier (required)
   * @param {string} [poData.po_id] - Purchase order identifier (auto-generated UUID if not provided)
   * @param {string} [poData.po_number] - Purchase order number (auto-generated if not provided)
   * @param {string} [poData.po_date] - PO date in format YYYY-MM-DD (default: current date)
   * @param {string} [poData.expected_delivery_date] - Expected delivery date in format YYYY-MM-DD
   * @param {string} [poData.status] - PO status: draft (default), approved, issued, closed, cancelled
   * @param {string} [poData.payment_status] - Payment status: unpaid (default), partial, paid
   * @param {string} [poData.currency] - Currency code (default: INR)
   * @param {string} [poData.site_id] - Site ID for filtering and association
   * @param {string} [poData.fob] - Free on Board location
   * @param {Object} [poData.billing_address] - Billing address object
   * @param {Object} [poData.shipping_address] - Shipping address object
   * @param {string} [poData.payment_terms] - Payment terms (e.g., "Net 30", "Net 45")
   * @param {number} [poData.freight_total] - Freight charges (default: 0)
   * @param {number} [poData.freight_charges] - Alternative field name for freight_total
   * @param {string} [poData.freight_notes] - Notes about freight
   * @param {string} [poData.notes] - General notes
   * @param {Array} poData.lines - Array of line items (required)
   * @param {string} [poData.lines[].line_id] - Line identifier (auto-generated as 001, 002, etc. if not provided)
   * @param {string} [poData.lines[].item_id] - Stock item identifier (required if sku not provided)
   * @param {string} [poData.lines[].sku] - SKU code (required if item_id not provided)
   * @param {string} [poData.lines[].description] - Item description (required if item_name not provided)
   * @param {string} [poData.lines[].item_name] - Item name (required if description not provided)
   * @param {string} poData.lines[].unit - Unit of measurement (kg, liters, pieces, etc.) (required)
   * @param {number} poData.lines[].quantity - Quantity ordered (must be > 0) (required)
   * @param {number} [poData.lines[].quantity_ordered] - Alternative field name for quantity
   * @param {number} poData.lines[].unit_cost - Unit cost/rate (must be > 0) (required)
   * @param {number} [poData.lines[].rate] - Alternative field name for unit_cost
   * @param {number} [poData.lines[].tax_rate] - Tax rate percentage (e.g., 5 for 5%)
   * @param {number} [poData.lines[].tax_amount] - Tax amount (auto-calculated from tax_rate if not provided)
   * @param {string} [poData.lines[].hsn_code] - HSN code for tax purposes
   * @param {string} [poData.lines[].expected_delivery_date] - Expected delivery date for this line item
   * @param {number} [poData.lines[].quantity_received] - Quantity received (default: 0)
   * @param {string} [poData.lines[].notes] - Line item notes
   * @returns {Promise<Object>} Object containing success status and response data
   */
  createPurchaseOrder: async (poData) => {
    try {
      // Validate required fields
      if (!poData.vendor_id) {
        throw new Error('vendor_id is required');
      }

      if (!poData.lines || !Array.isArray(poData.lines) || poData.lines.length === 0) {
        throw new Error('lines array is required and must not be empty');
      }

      // Prepare request body
      const requestBody = {
        vendor_id: poData.vendor_id,
      };

      // Add optional header fields
      if (poData.po_id) requestBody.po_id = poData.po_id;
      if (poData.po_number) requestBody.po_number = poData.po_number;
      if (poData.po_date) requestBody.po_date = poData.po_date;
      if (poData.expected_delivery_date) requestBody.expected_delivery_date = poData.expected_delivery_date;
      if (poData.status) requestBody.status = poData.status;
      if (poData.payment_status) requestBody.payment_status = poData.payment_status;
      if (poData.currency) requestBody.currency = poData.currency;
      if (poData.site_id) requestBody.site_id = poData.site_id; // Add site_id for filtering
      if (poData.fob) requestBody.fob = poData.fob;
      if (poData.billing_address) requestBody.billing_address = poData.billing_address;
      if (poData.shipping_address) requestBody.shipping_address = poData.shipping_address;
      if (poData.payment_terms) requestBody.payment_terms = poData.payment_terms;
      if (poData.freight_total !== undefined && poData.freight_total !== null) {
        requestBody.freight_total = parseFloat(poData.freight_total);
      } else if (poData.freight_charges !== undefined && poData.freight_charges !== null) {
        requestBody.freight_total = parseFloat(poData.freight_charges);
      }
      if (poData.freight_notes) requestBody.freight_notes = poData.freight_notes;
      if (poData.notes) requestBody.notes = poData.notes;

      // Process line items
      requestBody.lines = poData.lines.map((line) => {
        const lineItem = {};

        // Required fields validation
        if (!line.item_id && !line.sku) {
          throw new Error('Each line item must have either item_id or sku');
        }
        if (!line.description && !line.item_name) {
          throw new Error('Each line item must have either description or item_name');
        }
        if (!line.unit) {
          throw new Error('Each line item must have a unit');
        }
        if (!line.quantity && !line.quantity_ordered) {
          throw new Error('Each line item must have quantity or quantity_ordered');
        }
        if (!line.unit_cost && !line.rate) {
          throw new Error('Each line item must have unit_cost or rate');
        }

        // Validate numeric values
        const quantity = parseFloat(line.quantity || line.quantity_ordered);
        const unitCost = parseFloat(line.unit_cost || line.rate);

        if (isNaN(quantity) || quantity <= 0) {
          throw new Error('Line item quantity must be a positive number');
        }
        if (isNaN(unitCost) || unitCost <= 0) {
          throw new Error('Line item unit_cost must be a positive number');
        }

        // Add line item fields
        if (line.line_id) lineItem.line_id = line.line_id;
        if (line.item_id) lineItem.item_id = line.item_id;
        if (line.sku) lineItem.sku = line.sku;
        if (line.description) lineItem.description = line.description;
        if (line.item_name) lineItem.item_name = line.item_name;
        lineItem.unit = line.unit;
        lineItem.quantity = quantity;
        if (line.quantity_ordered) lineItem.quantity_ordered = quantity;
        lineItem.unit_cost = unitCost;
        if (line.rate) lineItem.rate = unitCost;
        if (line.tax_rate !== undefined && line.tax_rate !== null) {
          lineItem.tax_rate = parseFloat(line.tax_rate);
        }
        if (line.tax_amount !== undefined && line.tax_amount !== null) {
          lineItem.tax_amount = parseFloat(line.tax_amount);
        }
        if (line.hsn_code) lineItem.hsn_code = line.hsn_code;
        if (line.expected_delivery_date) lineItem.expected_delivery_date = line.expected_delivery_date;
        if (line.quantity_received !== undefined && line.quantity_received !== null) {
          lineItem.quantity_received = parseFloat(line.quantity_received);
        }
        if (line.notes) lineItem.notes = line.notes;

        return lineItem;
      });

      console.log(
        'Purchase Order API: Creating purchase order with payload:',
        JSON.stringify(requestBody, null, 2)
      );

      const response = await makeApiRequest(
        '/stock/purchase-orders',
        'POST',
        requestBody
      );
      const responseData = await response.json();

      console.log('Purchase Order API: Create response:', responseData);

      return {
        success: true,
        data: responseData,
        purchase_order: responseData.purchase_order,
        lines: responseData.lines,
      };
    } catch (error) {
      console.error('Purchase Order API: Error in createPurchaseOrder:', error);

      if (error.code === 'AUTHENTICATION_FAILED') {
        return {
          success: false,
          error: 'Authentication failed',
          message: 'Authentication token is missing. Please log in again.',
        };
      }

      return {
        success: false,
        error: error.responseData?.error || 'API Error',
        message:
          error.responseData?.message ||
          error.message ||
          'Failed to create purchase order',
        status: error.status,
      };
    }
  },

  /**
   * Fetches purchase orders with optional filtering parameters.
   * GET /stock/purchase-orders
   * @param {Object} params - Optional parameters for filtering purchase orders
   * @param {number} [params.limit] - Maximum number of purchase orders to return
   * @param {string} [params.last_key] - Pagination key for next page (JSON-encoded DynamoDB token)
   * @param {string} [params.vendor_id] - Filter by vendor identifier
   * @param {string} [params.status] - Filter by PO status: draft, approved, issued, closed, cancelled
   * @param {string} [params.payment_status] - Filter by payment status: unpaid, partial, paid
   * @param {string} [params.po_id] - Filter by specific purchase order ID
   * @param {string} [params.po_number] - Filter by purchase order number
   * @param {string} [params.start_date] - Filter POs created/dated on or after this date (YYYY-MM-DD)
   * @param {string} [params.end_date] - Filter POs created/dated on or before this date (YYYY-MM-DD)
   * @param {string} [params.po_date] - Filter by specific PO date (YYYY-MM-DD)
   * @param {string} [params.expected_delivery_date] - Filter by expected delivery date (YYYY-MM-DD)
   * @param {string} [params.created_after] - Filter POs created after this date (YYYY-MM-DD)
   * @param {string} [params.created_before] - Filter POs created before this date (YYYY-MM-DD)
   * @returns {Promise<Object>} Object containing success status and purchase orders data
   */
  getPurchaseOrders: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();

      // Add all supported query parameters
      if (params.limit !== undefined && params.limit !== null) {
        queryParams.append('limit', params.limit.toString());
      }
      if (params.last_key) {
        // If last_key is an object, stringify it; if it's already a string, use it as is
        const lastKeyValue =
          typeof params.last_key === 'string'
            ? params.last_key
            : JSON.stringify(params.last_key);
        queryParams.append('last_key', lastKeyValue);
      }
      if (params.vendor_id) {
        queryParams.append('vendor_id', params.vendor_id);
      }
      if (params.status) {
        queryParams.append('status', params.status);
      }
      if (params.payment_status) {
        queryParams.append('payment_status', params.payment_status);
      }
      if (params.po_id) {
        queryParams.append('po_id', params.po_id);
      }
      if (params.po_number) {
        queryParams.append('po_number', params.po_number);
      }
      if (params.start_date) {
        queryParams.append('start_date', params.start_date);
      }
      if (params.end_date) {
        queryParams.append('end_date', params.end_date);
      }
      if (params.site_id) {
        queryParams.append('site_id', params.site_id);
      }
      if (params.po_date) {
        queryParams.append('po_date', params.po_date);
      }
      if (params.expected_delivery_date) {
        queryParams.append('expected_delivery_date', params.expected_delivery_date);
      }
      if (params.created_after) {
        queryParams.append('created_after', params.created_after);
      }
      if (params.created_before) {
        queryParams.append('created_before', params.created_before);
      }

      // Build endpoint with query parameters
      const queryString = queryParams.toString();
      const endpoint = `/stock/purchase-orders${queryString ? `?${queryString}` : ''}`;

      console.log(
        'Purchase Order API: Fetching purchase orders with params:',
        JSON.stringify(Object.fromEntries(queryParams), null, 2)
      );

      const response = await makeApiRequest(endpoint, 'GET');
      const responseData = await response.json();

      console.log('Purchase Order API: Get purchase orders response:', responseData);

      // Extract purchase orders array from various possible response structures
      let purchaseOrdersArray = null;
      
      if (Array.isArray(responseData)) {
        // Response is directly an array
        purchaseOrdersArray = responseData;
      } else if (Array.isArray(responseData.orders)) {
        // API returns orders array (most common structure)
        purchaseOrdersArray = responseData.orders;
      } else if (Array.isArray(responseData.purchase_orders)) {
        purchaseOrdersArray = responseData.purchase_orders;
      } else if (Array.isArray(responseData.items)) {
        purchaseOrdersArray = responseData.items;
      } else if (Array.isArray(responseData.data)) {
        purchaseOrdersArray = responseData.data;
      } else if (responseData.purchase_orders && Array.isArray(responseData.purchase_orders)) {
        purchaseOrdersArray = responseData.purchase_orders;
      } else {
        // Default to empty array if structure is unexpected
        console.warn('Purchase Order API: Unexpected response structure, defaulting to empty array:', responseData);
        purchaseOrdersArray = [];
      }

      return {
        success: true,
        data: responseData,
        purchase_orders: purchaseOrdersArray,
        last_key: responseData.last_key,
        count: responseData.count || (Array.isArray(purchaseOrdersArray) ? purchaseOrdersArray.length : 0),
      };
    } catch (error) {
      console.error('Purchase Order API: Error in getPurchaseOrders:', error);

      if (error.code === 'AUTHENTICATION_FAILED') {
        return {
          success: false,
          error: 'Authentication failed',
          message: 'Authentication token is missing. Please log in again.',
        };
      }

      return {
        success: false,
        error: error.responseData?.error || 'API Error',
        message:
          error.responseData?.message ||
          error.message ||
          'Failed to fetch purchase orders',
        status: error.status,
      };
    }
  },

  /**
   * Get a single purchase order by po_id
   * GET /stock/purchase-orders/{po_id}
   * @param {string} po_id - Purchase order ID
   * @returns {Promise<Object>} Object containing success status and purchase order data with lines
   */
  getPurchaseOrder: async (po_id) => {
    try {
      if (!po_id) {
        throw new Error('po_id is required');
      }

      console.log('Purchase Order API: Getting single purchase order', { po_id });

      const endpoint = `/stock/purchase-orders/${po_id}`;
      const response = await makeApiRequest(endpoint, 'GET');
      const responseData = await response.json();

      console.log('Purchase Order API: Get single purchase order response:', responseData);

      if (responseData.purchase_order) {
        return {
          success: true,
          purchaseOrder: responseData.purchase_order,
          lines: responseData.lines || [],
        };
      } else {
        throw new Error('Purchase order not found in response');
      }
    } catch (error) {
      console.error('Purchase Order API: Error in getPurchaseOrder:', error);
      return {
        success: false,
        error: error.responseData?.error || 'API Error',
        message: error.message || 'Failed to fetch purchase order',
        status: error.status,
      };
    }
  },
};

